import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Globe, MapPin, User, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { TeamMember } from "./types";
import { TeamMembersList } from "./TeamMembersList";

interface StartupInfoCardProps {
  teamSize: number;
  websiteUrl?: string | null;
  location: string;
  founder: {
    id: string;
    username: string | null;
    full_name: string | null;
    is_pro: boolean;
  };
  startupId: string;
}

export const StartupInfoCard = ({ teamSize: initialTeamSize, websiteUrl, location, founder, startupId }: StartupInfoCardProps) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamSize, setTeamSize] = useState(initialTeamSize);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      console.log('Fetching team members for startup:', startupId);
      
      const { data: startup, error } = await supabase
        .from('startups')
        .select(`
          team_members,
          startup_roles (
            id,
            title
          )
        `)
        .eq('id', startupId)
        .single();

      if (error) {
        console.error('Error fetching team members:', error);
        return;
      }

      console.log('Fetched startup data:', startup);

      if (startup?.team_members && Array.isArray(startup.team_members)) {
        const memberIds = startup.team_members.map((member: any) => member.user_id);
        
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, full_name, avatar_url')
          .in('id', memberIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          return;
        }

        console.log('Fetched profiles:', profiles);

        const membersWithProfiles = startup.team_members.map((member: any) => ({
          ...member,
          profile: profiles?.find(p => p.id === member.user_id),
          role: startup.startup_roles?.find((r: any) => r.id === member.role_id)
        }));

        console.log('Processed team members:', membersWithProfiles);
        setTeamMembers(membersWithProfiles);
        setTeamSize(membersWithProfiles.length);
      }
    };

    fetchTeamMembers();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('team_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'startups',
          filter: `id=eq.${startupId}`
        },
        (payload) => {
          console.log('Received real-time update:', payload);
          fetchTeamMembers();
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up subscription');
      channel.unsubscribe();
    };
  }, [startupId]);

  return (
    <Card className="bg-flux-black/50 border-flux-cyan/20">
      <CardHeader>
        <CardTitle className="text-xl text-flux-cyan">Quick Info</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 text-gray-300">
          <MapPin className="w-5 h-5" />
          <span>{location}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-300">
          <User className="w-5 h-5" />
          <span>Founded by </span>
          <Link 
            to={`/profile/${founder.id}`}
            className="text-flux-cyan hover:underline"
          >
            {founder.full_name || founder.username || "Anonymous"}
          </Link>
          {founder.is_pro && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <BadgeCheck className="h-5 w-5 text-yellow-500 drop-shadow-[0_0_5px_rgba(234,179,8,0.5)] animate-pulse filter brightness-125" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Pro Member</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <div className="flex items-center gap-2 text-gray-300 cursor-pointer hover:text-flux-cyan transition-colors">
              <Users className="w-5 h-5" />
              <span>{teamSize} team members</span>
            </div>
          </DialogTrigger>
          <DialogContent className="bg-flux-black border-flux-cyan/20">
            <DialogHeader>
              <DialogTitle className="text-flux-cyan">Team Members</DialogTitle>
            </DialogHeader>
            <TeamMembersList members={teamMembers} />
          </DialogContent>
        </Dialog>
        {websiteUrl && (
          <div className="flex items-center gap-2 text-gray-300">
            <Globe className="w-5 h-5" />
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-flux-cyan hover:underline"
            >
              Visit Website
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
};